import type { DutyAnalyticsCardKind, DutyAnalyticsMetric } from '@pams/shared';
import { FIGURE_COLUMNS, MARKING_COLUMNS } from '@/lib/dutyAnalytics';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C.1 §3] WHICH NOTES EACH INFO DIALOG CARRIES — names only; every WORD comes from `DUTY_ANALYTICS_COPY`, so a
 *  dialog's text is the hidden notes card's text by construction.
 *
 *  ⚠️ A CARD NAMES THE FIGURES IT SHOWS, IN ITS OWN ORDER — "Left open and declared short" shows left open first,
 *  so its dialog does too. A figures table names its seven columns in column order, and the markings band its two:
 *  every layout renders one table and the band, so every layout reaches all nine notes.
 *
 *  ⚠️ THE LAYOUT NOTE goes on the commissionerate's two tables and on a division's Units table when the division is
 *  read from the commissionerate — never on a division nodal's own page, where it explains a page they cannot open.
 *  `ordersNote` closes every dialog.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type FigureInfoContent = {
  readonly metrics: readonly DutyAnalyticsMetric[];
  readonly layoutNote: boolean;
};

export const CARD_INFO: Record<DutyAnalyticsCardKind, readonly DutyAnalyticsMetric[]> = {
  MOST_ASKED: ['ASKED'],
  BEST_MAPPING: ['NAMED', 'ASKED'],
  ASKED_AND_NAMED: ['ASKED', 'NAMED'],
  OPEN_AND_DECLARED: ['LEFT_OPEN', 'DECLARED_SHORT'],
};

export const cardInfo = (kind: DutyAnalyticsCardKind): FigureInfoContent => ({
  metrics: CARD_INFO[kind],
  layoutNote: false,
});

/** A seven-column figures table's dialog — with the layout note where a division reads differently elsewhere. */
export const tableInfo = (layoutNote: boolean): FigureInfoContent => ({
  metrics: FIGURE_COLUMNS.map((column) => column.metric),
  layoutNote,
});

export const DELEGATION_INFO: FigureInfoContent = { metrics: ['DELEGATED'], layoutNote: false };

export const MARKINGS_INFO: FigureInfoContent = {
  metrics: MARKING_COLUMNS.map((column) => column.metric),
  layoutNote: false,
};
